import { Component, EventEmitter, forwardRef, Input, Output, signal } from '@angular/core';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterOperator,
  GalaxyFilterValueInterface,
} from '@vendasta/galaxy/filter/chips';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, debounceTime, startWith } from 'rxjs';

type SelectionType = 'single' | 'range';

@Component({
  templateUrl: './duration-custom-filter-input.component.html',
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => DurationCustomFilterInputComponent),
    },
  ],
  styleUrls: ['./duration-custom-filter-input.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
  ],
})
export class DurationCustomFilterInputComponent implements GalaxyAbstractFilterInput {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }

  @Input() set operator(value: GalaxyFilterOperator) {
    this.setSelectionType(value);
  }

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length === 1 && value[0].integer != null) {
      this.fromMilliseconds(value[0].integer, this.formGroup);
    } else if (value?.length === 2) {
      this.fromMilliseconds(value[0]?.integer ?? 0, this.formGroup);
      this.fromMilliseconds(value[1]?.integer ?? 0, this.formGroupEnd);
    }
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();

  formGroup: FormGroup = new FormBuilder().group({
    hour: 0,
    minute: 0,
    second: 0,
  });

  formGroupEnd: FormGroup = new FormBuilder().group({
    hour: 0,
    minute: 0,
    second: 0,
  });

  searchTerm = toSignal(this.formGroup.valueChanges);
  _fieldId = signal('');
  readonly selectionType$$ = new BehaviorSubject<SelectionType>('single');

  clearValue(): void {
    this.formGroup.controls['hour'].setValue(0);
    this.formGroup.controls['minute'].setValue(0);
    this.formGroup.controls['second'].setValue(0);
    this.formGroupEnd.controls['hour'].setValue(0);
    this.formGroupEnd.controls['minute'].setValue(0);
    this.formGroupEnd.controls['second'].setValue(0);
  }

  constructor() {
    combineLatest([
      this.selectionType$$,
      this.formGroup.valueChanges.pipe(startWith(this.formGroup.value)),
      this.formGroupEnd.valueChanges.pipe(startWith(this.formGroupEnd.value)),
    ])
      .pipe(takeUntilDestroyed(), debounceTime(100))
      .subscribe(([selectionType, formGroup, formGroupEnd]) => {
        if (selectionType === 'single') {
          this.valueChanges.emit([{ integer: this.toMilliseconds(formGroup) }]);
        } else {
          this.valueChanges.emit([
            { integer: this.toMilliseconds(formGroup) },
            { integer: this.toMilliseconds(formGroupEnd) },
          ]);
        }
      });
  }

  private setSelectionType(operator: GalaxyFilterOperator): void {
    const rangeOperators = [
      GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN,
      GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_BETWEEN,
    ];
    this.selectionType$$.next(rangeOperators.includes(operator) ? 'range' : 'single');
  }

  toMilliseconds(duration: Partial<{ hour?: number; minute?: number; second?: number }>): number {
    const hours = duration.hour ?? 0;
    const minutes = duration.minute ?? 0;
    const seconds = duration.second ?? 0;
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }

  fromMilliseconds(milliseconds: number, formGroup: FormGroup): void {
    const seconds = Math.floor((milliseconds / 1000) % 60);
    const minutes = Math.floor((milliseconds / (1000 * 60)) % 60);
    const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
    formGroup.controls['hour'].setValue(hours);
    formGroup.controls['minute'].setValue(minutes);
    formGroup.controls['second'].setValue(seconds);
  }
}
